import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Send, DollarSign, Eye, Bell,
  RefreshCw, Pencil, Ban, CheckCircle2, X, RotateCcw, Clock,
  AlertTriangle, ChevronDown, ChevronUp, History, Shield,
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PDFPreview from '../../components/PDFPreview';
import { toast } from '../../components/ui/sonner';

const fmt = (n, c) => {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c || 'USD' }).format(n); }
  catch { return `${c} ${Number(n)?.toFixed(2)}`; }
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const fmtDateTime = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
};

const ACTION_STYLE = {
  payment_reversed:      { dot: 'bg-amber-500', text: 'text-amber-700 bg-amber-50',   label: 'Reversed'    },
  invoice_voided:        { dot: 'bg-red-500',   text: 'text-red-700 bg-red-50',       label: 'Voided'      },
  bank_transaction_reversed: { dot: 'bg-amber-500', text: 'text-amber-700 bg-amber-50', label: 'Reversed' },
};
const getActionStyle = (type = '') => {
  if (ACTION_STYLE[type]) return ACTION_STYLE[type];
  if (type.includes('created') || type.includes('sent') || type.includes('opened'))
    return { dot: 'bg-emerald-500', text: 'text-emerald-700 bg-emerald-50', label: type };
  if (type.includes('updated') || type.includes('paid') || type.includes('recorded'))
    return { dot: 'bg-blue-500', text: 'text-blue-700 bg-blue-50', label: type };
  if (type.includes('login') || type.includes('auth'))
    return { dot: 'bg-purple-500', text: 'text-purple-700 bg-purple-50', label: type };
  return { dot: 'bg-slate-400', text: 'text-slate-600 bg-slate-100', label: type };
};

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const { hasPermission, user } = useAuth();
  const canEdit  = hasPermission('invoices');
  const isAdmin  = user?.role === 'admin' || user?.role === 'system_admin';

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [actionLoading, setAction] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Payment panel
  const [showPaid, setShowPaid]           = useState(false);
  const [paidAmount, setPaidAmount]       = useState('');
  const [paymentAccountId, setPayAcct]    = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes]   = useState('');
  const [bankAccounts, setBankAccounts]   = useState([]);
  // A fresh idempotency key each time the panel is opened. Reusing one across
  // two deliberate payments would silently discard the second.
  const [paymentKey, setPaymentKey]       = useState('');
  const openPaymentPanel = () => {
    setPaymentKey(`pay-${invoiceId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setShowPaid(true);
    loadUnapplied();
  };

  // Cash already on account. Surfacing an unapplied balance without a way to
  // apply it just tells someone about a problem they cannot fix.
  const [unapplied, setUnapplied]   = useState([]);
  const [applyFrom, setApplyFrom]   = useState('');
  const [applyAmount, setApplyAmt]  = useState('');

  // Edit panel
  const [showEdit, setShowEdit]           = useState(false);
  const [editDueDate, setEditDueDate]     = useState('');
  const [editReference, setEditReference] = useState('');

  // Individual payment reversal
  const [showReverseModal, setReverseModal] = useState(false);
  const [reverseTarget, setReverseTarget]   = useState(null);
  const [reversalNote, setReversalNote]     = useState('');

  // Void invoice (reverse all payments)
  const [showVoidModal, setVoidModal]   = useState(false);
  const [voidNote, setVoidNote]         = useState('');

  // Payments ledger
  const [payments, setPayments]         = useState([]);
  const [paymentsLoading, setPayLoading] = useState(false);

  // Audit trail
  const [showAudit, setShowAudit]       = useState(false);
  const [auditLogs, setAuditLogs]       = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadPayments = useCallback(() => {
    setPayLoading(true);
    api.get(`/api/invoices/${invoiceId}/payments`)
      .then(r => setPayments(r.data || []))
      .catch(() => setPayments([]))
      .finally(() => setPayLoading(false));
  }, [invoiceId]);

  const loadUnapplied = () => {
    const inv = data?.invoice;
    const name = inv?.client_name || inv?.contact_name;
    api.get(`/api/payments/unapplied${name ? `?contact_name=${encodeURIComponent(name)}` : ''}`)
      .then(r => setUnapplied(r.data || []))
      .catch(() => setUnapplied([]));
  };

  const applyUnapplied = async () => {
    if (!applyFrom || !applyAmount) return;
    setAction('applying');
    try {
      await api.post(`/api/payments/${applyFrom}/allocate`, {
        allocations: [{
          invoice_id: invoiceId,
          amount: Number(applyAmount),
          notes: 'Applied from cash on account',
        }],
      });
      toast.success('Applied from cash on account');
      setApplyFrom(''); setApplyAmt(''); setShowPaid(false);
      load(); loadPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply that cash');
    } finally { setAction(''); }
  };

  const loadAudit = () => {
    setAuditLoading(true);
    api.get(`/api/invoices/${invoiceId}/audit-trail`)
      .then(r => setAuditLogs(r.data || []))
      .catch(() => setAuditLogs([]))
      .finally(() => setAuditLoading(false));
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/invoices/${invoiceId}`)
      .then(r => {
        setData(r.data);
        setEditDueDate(r.data.invoice?.due_date || '');
        setEditReference(r.data.invoice?.reference_number || '');
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => {
    load();
    loadPayments();
    api.get('/api/accounts/chart?account_type=asset&is_active=true')
      .then(r => setBankAccounts((r.data || []).filter(a => a.account_subtype === 'bank')))
      .catch(() => {});
  }, [load, loadPayments]);

  const toggleAudit = () => {
    if (!showAudit && auditLogs.length === 0) loadAudit();
    setShowAudit(p => !p);
  };

  const action = async (type) => {
    setAction(type);
    try {
      if (type === 'send' || type === 'resend') {
        await api.post(`/api/invoices/${invoiceId}/send`);
        toast.success(type === 'resend' ? 'Invoice resent' : 'Invoice sent');
      } else if (type === 'send_reminder') {
        await api.post(`/api/invoices/${invoiceId}/send-reminder`);
        toast.success('Reminder sent');
      } else if (type === 'issue') {
        const res = await api.post(`/api/invoices/${invoiceId}/issue`);
        toast.success(`Invoice ${res.data.invoice_number} issued`);
      } else if (type === 'mark_paid') {
        const res = await api.put(`/api/invoices/${invoiceId}`, {
          paid_amount_placeholder: Number(paidAmount),
          ...(paymentAccountId ? { payment_account_id: paymentAccountId } : {}),
          ...(paymentMethod ? { payment_method: paymentMethod } : {}),
          ...(paymentNotes ? { payment_notes: paymentNotes } : {}),
          // One key per opened panel: a double-clicked Confirm is one receipt,
          // and the server can only know that if we tell it.
          client_request_id: paymentKey,
        });
        setShowPaid(false); setPaidAmount(''); setPaymentMethod(''); setPaymentNotes('');
        toast.success(res.data?.payment?.duplicate
          ? 'That payment was already recorded'
          : 'Payment recorded');
        loadPayments();
      } else if (type === 'edit') {
        await api.put(`/api/invoices/${invoiceId}`, {
          due_date: editDueDate || undefined,
          reference_number: editReference || undefined,
        });
        setShowEdit(false);
        toast.success('Invoice updated');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setAction('');
    }
  };

  const confirmReversal = async () => {
    if (!reverseTarget) return;
    setAction('reversing');
    try {
      await api.post(`/api/invoices/payments/${reverseTarget.payment_id}/reverse`, {
        reversal_note: reversalNote || undefined,
      });
      toast.success(`Payment of ${fmt(reverseTarget.amount, reverseTarget.currency_code)} reversed`);
      setReverseModal(false); setReverseTarget(null); setReversalNote('');
      load(); loadPayments();
      if (showAudit) loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reversal failed');
    } finally { setAction(''); }
  };

  const confirmVoid = async () => {
    setAction('voiding');
    try {
      const res = await api.post(`/api/invoices/${invoiceId}/void`, {
        reversal_note: voidNote || undefined,
      });
      toast.success(`Invoice voided — ${res.data.payments_reversed} payment(s) reversed`);
      setVoidModal(false); setVoidNote('');
      load(); loadPayments();
      if (showAudit) loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally { setAction(''); }
  };

  if (loading) return <div className="p-6"><div className="h-4 bg-slate-100 rounded animate-pulse w-48" /></div>;
  if (!data) return <div className="p-6 text-slate-500">Invoice not found</div>;

  const { invoice, line_items, client, reminder_events } = data;
  const displayName = client?.legal_name || invoice.client_name || invoice.contact_name || 'Unknown';
  const isDraft     = invoice.status === 'draft';
  const isFinished  = ['paid', 'voided', 'cancelled'].includes(invoice.status);
  // A draft has no invoice number and has been sent to nobody, so it is not
  // something that can be emailed, chased or paid. The server refuses all
  // three; the UI should not offer them either.
  const isSendable  = ['outstanding', 'overdue', 'partially_paid',
                       'partially_paid_overdue'].includes(invoice.status);
  const isChaseable = isSendable && invoice.balance_due_amount_placeholder > 0;

  const activePayments = payments.filter(p => !p.is_reversed && !p.is_contra);
  const canVoid = canEdit && !isDraft && !['voided', 'cancelled'].includes(invoice.status);

  return (
    <div className="p-6 fade-in max-w-4xl" data-testid="invoice-detail-page">

      {/* ── Individual Payment Reversal Modal ──────────────────────────────── */}
      {showReverseModal && reverseTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="reversal-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <RotateCcw size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Reverse Payment</h3>
                <p className="text-xs text-slate-500">Permanent — creates a contra-entry, audit-logged</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className="font-bold font-mono">{fmt(reverseTarget.amount, reverseTarget.currency_code)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date</span>
                <span className="font-mono">{fmtDate(reverseTarget.payment_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Reference</span>
                <span className="text-xs truncate max-w-48">{reverseTarget.reference || '—'}</span>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                A contra-entry of <strong>{fmt(-reverseTarget.amount, reverseTarget.currency_code)}</strong> will be posted.
                The original record is preserved for the audit trail.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-700 mb-1">Reason (optional)</label>
              <textarea rows={2} value={reversalNote} onChange={e => setReversalNote(e.target.value)}
                placeholder="e.g. Payment received in error..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="reversal-note-input" />
            </div>
            <div className="flex gap-3">
              <button onClick={confirmReversal} disabled={actionLoading === 'reversing'}
                className="flex-1 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-reversal-button">
                {actionLoading === 'reversing'
                  ? <><RefreshCw size={14} className="animate-spin" /> Reversing…</>
                  : <><RotateCcw size={14} /> Confirm Reversal</>}
              </button>
              <button onClick={() => { setReverseModal(false); setReverseTarget(null); }}
                className="px-4 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-50"
                data-testid="cancel-reversal-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Void Invoice Modal ─────────────────────────────────────────────── */}
      {showVoidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="void-invoice-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Ban size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Void Invoice</h3>
                <p className="text-xs text-slate-500">Reverses all payments and marks invoice as VOIDED</p>
              </div>
            </div>

            {activePayments.length > 0 ? (
              <>
                <p className="text-xs text-slate-600 mb-2 font-medium">
                  The following {activePayments.length} payment{activePayments.length !== 1 ? 's' : ''} will be reversed:
                </p>
                <div className="bg-slate-50 rounded-lg divide-y divide-slate-100 mb-4 max-h-36 overflow-y-auto">
                  {activePayments.map(p => (
                    <div key={p.payment_id} className="flex justify-between px-3 py-2 text-xs">
                      <span className="text-slate-600">{fmtDate(p.payment_date)}</span>
                      <span className="font-mono font-semibold text-slate-900">{fmt(p.amount, p.currency_code)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between px-3 py-2 text-xs font-bold border-t border-slate-200 bg-slate-100">
                    <span>Total reversed</span>
                    <span className="font-mono">{fmt(activePayments.reduce((a, p) => a + p.amount, 0), invoice.currency_code)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs text-slate-500">
                No active payments — invoice will be voided immediately.
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex gap-2">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800">
                This will mark the invoice as <strong>VOIDED</strong>.
                All original payment records are preserved for the audit trail.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-700 mb-1">Reason for voiding (optional)</label>
              <textarea rows={2} value={voidNote} onChange={e => setVoidNote(e.target.value)}
                placeholder="e.g. Duplicate invoice, contract cancelled..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="void-note-input" />
            </div>

            <div className="flex gap-3">
              <button onClick={confirmVoid} disabled={actionLoading === 'voiding'}
                className="flex-1 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-void-button">
                {actionLoading === 'voiding'
                  ? <><RefreshCw size={14} className="animate-spin" /> Voiding…</>
                  : <><Ban size={14} /> Void Invoice</>}
              </button>
              <button onClick={() => setVoidModal(false)}
                className="px-4 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-50"
                data-testid="cancel-void-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft size={15} /> Back to Invoices
        </Link>
        <div className="flex gap-2 flex-wrap">
          {canEdit && isSendable && (
            <button onClick={() => setShowPreview(true)} disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-900 text-white text-sm font-semibold rounded-md hover:bg-indigo-800 disabled:opacity-50"
              data-testid="preview-send-invoice-button">
              <Eye size={14} />
              {invoice.sent_at ? 'Preview & Resend' : 'Preview & Send'}
            </button>
          )}
          {canEdit && isSendable && invoice.sent_at && (
            <button onClick={() => action('resend')} disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800 disabled:opacity-50"
              data-testid="resend-invoice-button">
              <RefreshCw size={14} className={actionLoading === 'resend' ? 'animate-spin' : ''} />
              {actionLoading === 'resend' ? 'Resending…' : 'Resend Email'}
            </button>
          )}
          {canEdit && isChaseable && (
            <button onClick={() => action('send_reminder')} disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50"
              data-testid="send-reminder-button">
              <Bell size={14} /> Send Reminder
            </button>
          )}
          {canEdit && isDraft && (
            <button onClick={() => action('issue')} disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-900 text-white text-sm font-semibold rounded-md hover:bg-indigo-800 disabled:opacity-50"
              data-testid="issue-invoice-button">
              <Send size={14} />
              {actionLoading === 'issue' ? 'Issuing…' : 'Issue Invoice'}
            </button>
          )}
          {canEdit && isDraft && (
            <Link to={`/invoices/${invoiceId}/edit`}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50"
              data-testid="edit-draft-button">
              <Pencil size={14} /> Edit Draft
            </Link>
          )}
          {canEdit && !isFinished && !isDraft && (
            <button onClick={() => setShowEdit(!showEdit)} disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 disabled:opacity-50"
              data-testid="edit-invoice-button">
              <Pencil size={14} /> Edit
            </button>
          )}
          {canEdit && !isDraft && !['paid', 'voided', 'cancelled'].includes(invoice.status) && (
            <button onClick={() => (showPaid ? setShowPaid(false) : openPaymentPanel())}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
              data-testid="mark-paid-button">
              <DollarSign size={14} /> Record Payment
            </button>
          )}
          {canVoid && (
            <button onClick={() => setVoidModal(true)} disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 text-sm font-medium rounded-md hover:bg-red-100 disabled:opacity-50"
              data-testid="void-invoice-button">
              <Ban size={14} /> Void Invoice
            </button>
          )}
        </div>
      </div>

      {/* Edit panel */}
      {showEdit && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-5">
          <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Pencil size={14}/> Edit Invoice</p>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Due Date</label>
              <input type="date" value={editDueDate} onChange={e => setEditDueDate(e.target.value)}
                className="border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="edit-due-date-input" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-slate-500 mb-1">Reference / PO Number</label>
              <input type="text" value={editReference} onChange={e => setEditReference(e.target.value)}
                placeholder="e.g. PO-12345"
                className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="edit-reference-input" />
            </div>
            <button onClick={() => action('edit')} disabled={!!actionLoading}
              className="px-4 py-1.5 bg-indigo-900 text-white text-sm rounded-md hover:bg-indigo-800 disabled:opacity-50"
              data-testid="confirm-edit-button">
              {actionLoading === 'edit' ? 'Saving…' : 'Save Changes'}
            </button>
            <button onClick={() => setShowEdit(false)} className="text-slate-500"><X size={16}/></button>
          </div>
        </div>
      )}

      {/* Payment panel */}
      {showPaid && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-5" data-testid="payment-panel">
          <p className="text-sm font-semibold text-green-800 mb-1 flex items-center gap-2">
            <CheckCircle2 size={14}/> Record Payment
          </p>
          <p className="text-xs text-green-700 mb-3">
            Balance due: <strong>{fmt(invoice.balance_due_amount_placeholder, invoice.currency_code)}</strong>.
            An immutable payment ledger entry will be created.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <input type="number" min={0.01} step={0.01}
              placeholder={`Amount (max ${invoice.balance_due_amount_placeholder})`}
              value={paidAmount} onChange={e => setPaidAmount(e.target.value)}
              className="border border-slate-200 rounded px-3 py-1.5 text-sm w-48"
              data-testid="paid-amount-input" />
            <select value={paymentAccountId} onChange={e => setPayAcct(e.target.value)}
              className="border border-slate-200 rounded px-3 py-1.5 text-sm"
              data-testid="payment-bank-account-select">
              <option value="">-- Select bank account --</option>
              {bankAccounts.map(a => (
                <option key={a.account_id} value={a.account_id}>{a.account_code} — {a.account_name}</option>
              ))}
            </select>
            {/* The bank account says where the cash landed; the method says
                how it travelled. They are different facts and the table had
                nowhere to put the second until now. */}
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
              className="border border-slate-200 rounded px-3 py-1.5 text-sm"
              data-testid="payment-method-select">
              <option value="">-- Method --</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="card">Card</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="direct_debit">Direct debit</option>
              <option value="other">Other</option>
            </select>
            <input type="text" value={paymentNotes}
              onChange={e => setPaymentNotes(e.target.value)}
              placeholder="Notes (e.g. cheque number)"
              className="border border-slate-200 rounded px-3 py-1.5 text-sm w-56"
              data-testid="payment-notes-input" />
            <button onClick={() => action('mark_paid')} disabled={!paidAmount || !!actionLoading}
              className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
              data-testid="confirm-paid-button">
              {actionLoading === 'mark_paid' ? 'Saving…' : 'Confirm Payment'}
            </button>
            <button onClick={() => setShowPaid(false)} className="text-slate-500"><X size={16}/></button>
          </div>

          {/* Cash already received from this customer that has not been told
              which invoice it settles. Applying it moves no money and posts
              nothing — the receipt was posted when it arrived. */}
          {unapplied.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200" data-testid="unapplied-cash-panel">
              <p className="text-xs font-semibold text-green-800 mb-2">
                Or apply cash already on account
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <select value={applyFrom} onChange={e => {
                  setApplyFrom(e.target.value);
                  const p = unapplied.find(u => u.payment_id === e.target.value);
                  const cap = Math.min(p?.unapplied ?? 0, invoice.balance_due_amount_placeholder);
                  setApplyAmt(cap > 0 ? String(cap) : '');
                }}
                  className="border border-slate-200 rounded px-3 py-1.5 text-sm"
                  data-testid="unapplied-payment-select">
                  <option value="">-- Unapplied receipt --</option>
                  {unapplied.map(u => (
                    <option key={u.payment_id} value={u.payment_id}>
                      {u.payment_date} · {fmt(u.unapplied, u.currency_code)} unapplied
                      {u.reference ? ` · ${u.reference}` : ''}
                    </option>
                  ))}
                </select>
                <input type="number" min={0.01} step={0.01} value={applyAmount}
                  onChange={e => setApplyAmt(e.target.value)}
                  placeholder="Amount to apply"
                  className="border border-slate-200 rounded px-3 py-1.5 text-sm w-44"
                  data-testid="apply-amount-input" />
                <button onClick={applyUnapplied}
                  disabled={!applyFrom || !applyAmount || !!actionLoading}
                  className="px-4 py-1.5 bg-slate-700 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50"
                  data-testid="confirm-apply-button">
                  {actionLoading === 'applying' ? 'Applying…' : 'Apply'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invoice header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-mono">{invoice.invoice_number}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{displayName} &mdash; Due: {invoice.due_date || 'N/A'}</p>
          {invoice.reference_number && <p className="text-xs text-slate-400 mt-0.5">Ref: {invoice.reference_number}</p>}
        </div>
        <span className={`px-3 py-1 rounded text-sm font-medium badge-${invoice.status}`}>
          {invoice.status.replace('_', ' ')}
        </span>
      </div>

      {/* Financial summary */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total',       value: fmt(invoice.total_amount, invoice.currency_code) },
          { label: 'Paid',        value: fmt(invoice.paid_amount_placeholder, invoice.currency_code) },
          { label: 'Balance Due', value: fmt(invoice.balance_due_amount_placeholder, invoice.currency_code),
            highlight: invoice.balance_due_amount_placeholder > 0 && invoice.status !== 'paid' },
          { label: 'Due Date',    value: invoice.due_date || '—' },
        ].map(({ label, value, highlight }) => (
          <div key={label} className={`bg-white border rounded-lg p-4 ${highlight ? 'border-red-200' : 'border-slate-200'}`}>
            <p className="text-xs text-slate-500 mb-1">{label}</p>
            <p className={`text-lg font-bold font-mono ${highlight ? 'text-red-700' : 'text-slate-900'}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Line Items */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h2 className="font-semibold text-sm text-slate-700">Line Items</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              {['#', 'Code', 'Description', 'Qty', 'Unit Price', 'Amount'].map(h => (
                <th key={h} className={`px-5 py-2.5 text-xs text-slate-500 font-medium ${['Qty','Unit Price','Amount'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(line_items || []).length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-4 text-center text-slate-400 text-xs">No line items</td></tr>
            ) : (
              (line_items || []).map(item => (
                <tr key={item.line_item_id}>
                  <td className="px-5 py-3 text-xs text-slate-400 font-mono">{item.line_number}</td>
                  <td className="px-5 py-3 text-xs font-mono text-slate-600">{item.product_code_snapshot || item.account_code || '—'}</td>
                  <td className="px-5 py-3 text-slate-900">{item.description_snapshot}</td>
                  <td className="px-5 py-3 text-right font-mono text-xs">{item.quantity}</td>
                  <td className="px-5 py-3 text-right font-mono text-xs">{fmt(item.unit_price, invoice.currency_code)}</td>
                  <td className="px-5 py-3 text-right font-mono font-medium text-slate-900">{fmt(item.line_subtotal_amount, invoice.currency_code)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <div className="w-52 space-y-1 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal</span><span className="font-mono">{fmt(invoice.subtotal_amount, invoice.currency_code)}</span>
            </div>
            {invoice.discount_amount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Discount</span><span className="font-mono">-{fmt(invoice.discount_amount, invoice.currency_code)}</span>
              </div>
            )}
            {invoice.tax_amount > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Tax</span><span className="font-mono">{fmt(invoice.tax_amount, invoice.currency_code)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1">
              <span>Total</span><span className="font-mono">{fmt(invoice.total_amount, invoice.currency_code)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Payment Ledger ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4" data-testid="payment-ledger">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-sm text-slate-700">Payment Ledger</h2>
            <p className="text-xs text-slate-400 mt-0.5">Immutable record — reversals create contra-entries</p>
          </div>
          {payments.length > 0 && (
            <span className="text-xs text-slate-500 font-mono">
              {activePayments.length} active payment{activePayments.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {paymentsLoading ? (
          <div className="px-5 py-4">{[1, 2].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse mb-2 w-3/4" />)}</div>
        ) : payments.length === 0 ? (
          <div className="px-5 py-5 text-center text-slate-400 text-sm flex flex-col items-center gap-1">
            <Clock size={20} className="text-slate-300" />
            <span>No payments recorded yet</span>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="payment-ledger-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                {['Date', 'Reference', 'Type', 'Amount', 'Status', 'Action'].map((h, i) => (
                  <th key={h} className={`px-5 py-2.5 text-xs text-slate-500 font-medium ${i >= 3 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map(pay => {
                const isContra   = pay.is_contra;
                const isReversed = pay.is_reversed;
                return (
                  <tr key={pay.payment_id}
                    className={isReversed ? 'bg-red-50/30 opacity-60' : isContra ? 'bg-amber-50/40' : ''}
                    data-testid={`payment-row-${pay.payment_id}`}>
                    <td className={`px-5 py-3 text-xs font-mono ${isReversed ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                      {fmtDate(pay.payment_date)}
                    </td>
                    <td className={`px-5 py-3 text-xs ${isReversed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      <span className="truncate block max-w-[200px]" title={pay.reference}>{pay.reference || '—'}</span>
                    </td>
                    <td className="px-5 py-3">
                      {isContra
                        ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">CONTRA</span>
                        : pay.is_manual
                          ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">Manual</span>
                          : <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">Imported</span>}
                    </td>
                    <td className={`px-5 py-3 text-right font-mono text-xs font-semibold
                      ${isContra || pay.amount < 0 ? 'text-red-600' : 'text-emerald-700'}
                      ${isReversed ? 'line-through' : ''}`}>
                      {fmt(pay.amount, pay.currency_code)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {isReversed
                        ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">REVERSED</span>
                        : <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">Active</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {canEdit && !isReversed && !isContra && (
                        <button onClick={() => { setReverseTarget(pay); setReversalNote(''); setReverseModal(true); }}
                          className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium"
                          data-testid={`reverse-payment-${pay.payment_id}`}>
                          <RotateCcw size={11} /> Reverse
                        </button>
                      )}
                      {isContra && <span className="text-xs text-slate-400 italic">contra-entry</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Audit Trail ────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4" data-testid="invoice-audit-section">
        <button
          onClick={toggleAudit}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
          data-testid="toggle-audit-trail-button"
        >
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-slate-500" />
            <span className="font-semibold text-sm text-slate-700">Audit Trail</span>
            {!isAdmin && <span className="text-xs text-slate-400">(Admin only)</span>}
          </div>
          <div className="flex items-center gap-2">
            {auditLogs.length > 0 && (
              <span className="text-xs text-slate-400">{auditLogs.length} event{auditLogs.length !== 1 ? 's' : ''}</span>
            )}
            {showAudit ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </div>
        </button>

        {showAudit && (
          <div className="border-t border-slate-200 px-5 py-4" data-testid="audit-trail-content">
            {auditLoading ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
            ) : auditLogs.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3">No audit events recorded for this invoice</p>
            ) : (
              <ol className="relative border-l border-slate-200 space-y-0 ml-2">
                {[...auditLogs].reverse().map((log, idx) => {
                  const style = getActionStyle(log.action_type);
                  return (
                    <li key={log.log_id || idx} className="mb-4 ml-4" data-testid={`audit-event-${idx}`}>
                      <span className={`absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white ${style.dot}`} />
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${style.text}`}>
                              {log.action_type}
                            </span>
                            <span className="text-xs text-slate-400 font-mono">{fmtDateTime(log.created_at)}</span>
                          </div>
                          <p className="text-xs text-slate-600 leading-relaxed">{log.action_summary}</p>
                          {log.performed_by_email && !log.performed_by_system && (
                            <p className="text-xs text-slate-400 mt-0.5">By {log.performed_by_email}</p>
                          )}
                          {log.performed_by_system && (
                            <p className="text-xs text-slate-400 mt-0.5">System</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}
      </div>

      {/* Reminder History */}
      {reminder_events?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="px-5 py-3 border-b border-slate-200">
            <h2 className="font-semibold text-sm text-slate-700">Reminder History</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {reminder_events.map(ev => (
              <div key={ev.event_id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-700">{ev.message_subject_snapshot}</p>
                  <p className="text-xs text-slate-500 mt-0.5">To: {ev.recipient_email}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${ev.status === 'sent' ? 'badge-active' : 'badge-inactive'}`}>{ev.status}</span>
                  <p className="text-xs text-slate-400 mt-0.5 font-mono">{new Date(ev.sent_at || ev.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPreview && (
        <PDFPreview
          type="invoice"
          doc={data?.invoice}
          lineItems={data?.line_items || []}
          client={data?.client}
          clientName={displayName}
          onClose={() => setShowPreview(false)}
          onConfirm={async () => {
            setShowPreview(false);
            await action(invoice.status === 'sent' ? 'resend' : 'send');
          }}
          confirmLabel={invoice.status === 'sent' ? 'Confirm & Resend Invoice' : 'Confirm & Send Invoice'}
        />
      )}
    </div>
  );
}
